/**
 * Attention Required Activities Hook
 *
 * React Query hook for fetching activities that require human attention.
 * Used by AttentionDetail component in the Control Hub.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { ActivitiesListResponse } from "@/data/api/activities";

// ============================================================================
// Query Keys
// ============================================================================

export interface AttentionRequiredQueryKeyParts {
    limit: number;
    startDate?: string | null;
    endDate?: string | null;
    carrierScac?: string;
    shipmentStatus?: string;
    containerDataOnly?: boolean;
}

export const ATTENTION_REQUIRED_QUERY_KEYS = {
    all: ["attention-required"] as const,
    list: (p: AttentionRequiredQueryKeyParts) =>
        [
            ...ATTENTION_REQUIRED_QUERY_KEYS.all,
            "list",
            p.limit,
            p.startDate,
            p.endDate,
            p.carrierScac,
            p.shipmentStatus,
            p.containerDataOnly,
        ] as const,
};

// ============================================================================
// Hook
// ============================================================================

export interface UseAttentionRequiredOptions {
    limit?: number;
    startDate?: string | null;
    endDate?: string | null;
    carrierScac?: string;
    shipmentStatus?: string;
    /** When true, only activities linked to containers with incomplete metadata */
    containerDataOnly?: boolean;
    enabled?: boolean;
}

/**
 * Hook to fetch activities that require attention
 *
 * Returns activities with attention_level set (action_needed or require_review), plus counts.
 */
export function useAttentionRequired(options: UseAttentionRequiredOptions = {}) {
    const {
        limit = 500,
        startDate,
        endDate,
        carrierScac,
        shipmentStatus,
        containerDataOnly,
        enabled = true,
    } = options;

    return useQuery({
        queryKey: ATTENTION_REQUIRED_QUERY_KEYS.list({
            limit,
            startDate,
            endDate,
            carrierScac,
            shipmentStatus,
            containerDataOnly,
        }),
        queryFn: async (): Promise<ActivitiesListResponse> => {
            const params: Record<string, string | number | boolean> = { limit };
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (carrierScac) params.carrier_scac = carrierScac;
            if (shipmentStatus) params.shipment_status = shipmentStatus;
            if (containerDataOnly === true) params.container_data_only = true;
            return await apiGet<ActivitiesListResponse>("/activities/attention-required", { params });
        },
        enabled,
        staleTime: 15 * 1000,
        refetchInterval: 30 * 1000,
    });
}
